import React, { useEffect, useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useItemQuantities from '@afosto/utils-react/useItemQuantities';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import { useDataSource } from '@afosto/app-components-react/DataSource';
import { useSnackbar } from 'notistack';
import { FormattedMessage } from 'react-intl';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import Grid from '@material-ui/core/Grid';
import AttachRow from './AttachRow';
import AttachRowSkeleton from './AttachRow/AttachRowSkeleton';
import DataList from '@afosto/app-components-react/DataList';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import OrderSearch from '@afosto/app-components-react/OrderSearch';
import Page from '@afosto/app-components-react/Page';
import Select from '@afosto/app-components-react/Select';
import WmsLocationSearch from '@afosto/app-components-react/WmsLocationSearch';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import fetchMultipleProducts from '@afosto/utils-react/fetchMultipleProducts';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import uuid from '@afosto/utils/uuid';
import { items as itemsApi } from '@afosto/order/api/Items';
import translations from './translations';

const AttachRowsContainer = props => <Grid container spacing={1} {...props} />;

const Attach = () => {
  usePrivate();

  const intl = useIntl();
  const [hasPermissions, UnauthorizedComponent] = usePermissions([
    'odr:orders:read',
    'odr:orders:write',
    'wms:item:read',
  ]);
  const { enqueueSnackbar } = useSnackbar();
  const [isAttachingItems, setIsAttachingItems] = useState(false);
  const [location, setLocation] = useState(null);
  const [order, setOrder] = useState(null);
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [ignoredSkus, setIgnoredSkus] = useState([]);
  const [inventoryOption, setInventoryOption] = useState('referenced');

  useEffect(() => {
    setIgnoredSkus([]);
  }, [location, order, purchaseOrder, inventoryOption]);

  const fetchItems = async dataSource =>
    dataSourceRequest(
      dataSource,
      async (source, requestOptions) => {
        if ((!order && !purchaseOrder) || !location) {
          return Promise.reject(new Error('No order or location configured'));
        }

        const useReferencedStock = inventoryOption === 'referenced';
        const useAvailableStock = inventoryOption === 'available';

        return itemsApi.searchGroupsWithAttach(
          {
            order: {
              constraints: [
                {
                  key: 'is_unavailable',
                  operator: 'in',
                  values: ['true'],
                },
                ...(purchaseOrder
                  ? [
                      {
                        key: 'is_backordered',
                        operator: 'in',
                        values: ['true'],
                      },
                      {
                        key: 'backorder_ids',
                        operator: 'in',
                        values: [purchaseOrder?.id],
                      },
                    ]
                  : []),
                ...(order
                  ? [
                      {
                        key: 'order_ids',
                        operator: 'in',
                        values: [order?.id],
                      },
                    ]
                  : []),
                ...(ignoredSkus?.length > 0
                  ? [
                      {
                        key: 'skus',
                        operator: 'not_in',
                        values: ignoredSkus,
                      },
                    ]
                  : []),
              ],
            },
            wms: {
              constraints: [
                {
                  key: 'state',
                  operator: 'in',
                  values: ['backordered', ...(useAvailableStock ? ['available'] : [])],
                },
                {
                  key: 'claim_id',
                  operator: 'eq',
                  values: [''],
                },
                {
                  key: 'location_id',
                  operator: 'in',
                  values: [location?.id],
                },
              ],
              use_referenced_stock: useReferencedStock,
            },
          },
          requestOptions,
        );
      },
      {
        transformData: async data => {
          try {
            const skus = [...new Set(data.map(item => item.sku))];
            const products = await fetchMultipleProducts(skus);

            return data.reduce((acc, item) => {
              const product = findObjectInArray(products, 'sku', item.sku);
              return [...acc, { ...item, product, reference: uuid() }];
            }, []);
          } catch (err) {
            return [];
          }
        },
      },
    );

  const dataSource = useDataSource(fetchItems, {
    modules: ['dataProperties'],
  });
  const { data } = dataSource;
  const expectedQuantities = (data ?? []).reduce(
    (acc, item) => ({
      ...acc,
      [item?.reference]: {
        requested: item?.quantity ?? 0,
        quantity: item?.inventory_item_ids?.length ?? 0,
        available: item?.inventory_item_ids?.length ?? 0,
        maxAvailable: item?.available ?? 0,
      },
    }),
    {},
  );

  const { itemQuantities, handleChange, handleMinus, handlePlus } = useItemQuantities(
    expectedQuantities,
    {
      onBeforeChange: (key, newValue) => {
        const expected = expectedQuantities[key];

        if (newValue > expected?.available) {
          return expected?.available ?? 0;
        }

        return newValue;
      },
      onBeforePlus: (key, newValue) => {
        const expected = expectedQuantities[key];

        if (newValue > expected?.available) {
          return Promise.reject();
        }

        return newValue;
      },
    },
  );

  const handleSelectOrder = (ev, selectedOrder) => {
    setOrder(selectedOrder);
    setInventoryOption('backordered');
    dataSource.reloadDataSource();
  };

  const handleSelectPurchaseOrder = (ev, selectedPurchaseOrder) => {
    setPurchaseOrder(selectedPurchaseOrder);
    setInventoryOption('referenced');
    dataSource.reloadDataSource();
  };

  const handleSelectLocation = (ev, selectedLocation) => {
    setLocation(selectedLocation);
    dataSource.reloadDataSource();
  };

  const handleChangeInventoryOption = (ev, selectedOption) => {
    setInventoryOption(selectedOption);
    dataSource.reloadDataSource();
  };

  const handleSaveAttachedItems = async () => {
    try {
      setIsAttachingItems(true);

      const unattachedSkus = Object.keys(itemQuantities).reduce((acc, reference) => {
        const { quantity = 0, requested = 0 } = itemQuantities[reference] ?? {};

        if (quantity >= requested && requested > 0) {
          return acc;
        }

        const item = findObjectInArray(data, 'reference', reference);

        if (item?.sku) {
          return [...acc, item.sku];
        }

        return acc;
      }, []);
      const attachData = Object.keys(itemQuantities).reduce((acc, reference) => {
        const { quantity = 0 } = itemQuantities[reference] ?? {};
        const item = findObjectInArray(data, 'reference', reference);
        const firstOrder = (item?.references?.orders ?? []).find(
          orderReference => !orderReference.is_inbound,
        );

        if (item && quantity > 0) {
          const itemIds = (item?.ids ?? []).slice(0, quantity);
          const formattedItems = itemIds.reduce((itemAcc, itemId, idx) => {
            const inventoryItemId = item?.inventory_item_ids?.[idx];

            if (inventoryItemId) {
              return [...itemAcc, { order_item_id: itemId, inventory_item_id: inventoryItemId }];
            }

            return itemAcc;
          }, []);

          if (formattedItems?.length) {
            return { ...acc, [firstOrder.id]: [...(acc[firstOrder.id] ?? []), ...formattedItems] };
          }
        }

        return acc;
      }, {});

      const promises = Object.keys(attachData).map(orderId =>
        itemsApi.attachWarehouseItems(orderId, attachData[orderId]),
      );

      await Promise.all(promises);

      setIgnoredSkus(currentIgnoredSkus => [
        ...new Set([...currentIgnoredSkus, ...unattachedSkus]),
      ]);

      if (Object.keys(attachData)?.length > 0) {
        enqueueSnackbar(intl.formatMessage(translations.feedback.itemsAttached), {
          variant: 'success',
        });
      }

      dataSource.reloadDataSource();
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.itemsNotAttached), {
        variant: 'error',
      });
    } finally {
      setIsAttachingItems(false);
    }
  };

  if (!hasPermissions()) {
    return (
      <Page title={intl.formatMessage(translations.title)} fullWidth>
        <UnauthorizedComponent showActions={false} />
      </Page>
    );
  }

  return (
    <Page title={intl.formatMessage(translations.title)} fullWidth>
      <LoadingWrapper isLoading={isAttachingItems}>
        <Box mb={4}>
          <Card>
            <Box py={1} px={2}>
              <WmsLocationSearch
                name="location"
                label={intl.formatMessage(translations.fields.location.label)}
                value={location}
                onChange={handleSelectLocation}
                disabled={dataSource.isLoadingData || isAttachingItems}
              />
              <OrderSearch
                name="order"
                label={intl.formatMessage(translations.fields.order.label)}
                value={order}
                onChange={handleSelectOrder}
                disabled={dataSource.isLoadingData || isAttachingItems || !!purchaseOrder}
                filters={[
                  {
                    key: 'type',
                    operator: 'in',
                    values: ['order'],
                  },
                ]}
                sort={[
                  {
                    key: 'date_created',
                    order: 'asc',
                  },
                ]}
              />
              <OrderSearch
                name="purchaseOrder"
                label={intl.formatMessage(translations.fields.purchaseOrder.label)}
                value={purchaseOrder}
                onChange={handleSelectPurchaseOrder}
                disabled={dataSource.isLoadingData || isAttachingItems || !!order}
                filters={[
                  {
                    key: 'type',
                    operator: 'in',
                    values: ['purchase'],
                  },
                ]}
                sort={[
                  {
                    key: 'date_created',
                    order: 'asc',
                  },
                ]}
              />
              <Box mt={1}>
                <Select
                  name="inventoryOption"
                  label={intl.formatMessage(translations.fields.inventoryOption.label)}
                  onChange={handleChangeInventoryOption}
                  value={inventoryOption}
                  disabled={
                    dataSource.isLoadingData || isAttachingItems || (!order && !purchaseOrder)
                  }
                  suggestions={[
                    ...(purchaseOrder
                      ? [
                          {
                            label: intl.formatMessage(translations.inventoryOptions.referenced),
                            value: 'referenced',
                          },
                        ]
                      : []),
                    {
                      label: intl.formatMessage(translations.inventoryOptions.backordered),
                      value: 'backordered',
                    },
                    {
                      label: intl.formatMessage(translations.inventoryOptions.available),
                      value: 'available',
                    },
                  ]}
                />
              </Box>
            </Box>
          </Card>
        </Box>
        {location && (order || purchaseOrder) && (
          <DataList
            component={AttachRowsContainer}
            dataSource={dataSource}
            emptyMessage={intl.formatMessage(translations.emptyMessage)}
            renderItem={item => (
              <AttachRow
                key={item?.reference}
                row={item}
                available={itemQuantities[item.reference]?.maxAvailable}
                requested={itemQuantities[item.reference]?.requested}
                quantity={itemQuantities[item.reference]?.quantity}
                onChange={handleChange(item.reference)}
                onMinus={handleMinus(item.reference)}
                onPlus={handlePlus(item.reference)}
              />
            )}
            renderSkeletonItem={key => <AttachRowSkeleton key={key} />}
          />
        )}
        <Box display="flex" my={2}>
          <Button
            onClick={handleSaveAttachedItems}
            color="primary"
            variant="contained"
            style={{ marginLeft: 'auto' }}
          >
            <FormattedMessage {...translations.actions.save} />
          </Button>
        </Box>
      </LoadingWrapper>
    </Page>
  );
};

export default Attach;
